import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import { Button, FormControl, Panel, Table, Well } from 'react-bootstrap';
import classnames from 'classnames';
import dayjs from '../util/date.js';
import LabeledCheckbox from './LabeledCheckbox.jsx';
import ScheduleVersionList from './ScheduleVersionList.jsx';
import Utils from '../util/Utils.jsx';
import { trackEvent } from '../util/Analytics.jsx';
import { storeOfflineDataset, removeOfflineDataset, getOfflineChanges } from '../offline-features/db.js';
import { useStateValue } from '../context/state.jsx';
import { openAlert, openPrompt } from '../context/alert.js';
import { focusProject, sourceFormatLabel } from '../util/openProject.js';

const HUB_SECTIONS = [
	{ id: 'overview', label: 'Overview' },
	{ id: 'files', label: 'Files & map' },
	{ id: 'schedule', label: 'Schedule' },
	{ id: 'versions', label: 'Versions' },
	{ id: 'people', label: 'People' },
	{ id: 'offline', label: 'Offline' },
	{ id: 'danger', label: 'Danger zone' }
];

const ProjectHub = ({
	datasetId,
	permissions,
	setUserPermission,
	updateDatasetMetadata,
	deleteProject,
	setEditingDataset,
	isOnline,
	updateAreaProperty
}) => {
	const [{ datasets, currentDataset }, dispatch] = useStateValue();
	const navigate = useNavigate();
	const [activeSection, setActiveSection] = useState('overview');
	const [relatedUsers, setRelatedUsers] = useState([]);
	const [relatedUsersLoading, setRelatedUsersLoading] = useState(false);
	const [userSearchTerm, setUserSearchTerm] = useState('');
	const [offlineChanges, setOfflineChanges] = useState([]);
	const [deleteConfirm, setDeleteConfirm] = useState('');

	const dataset = datasets?.[datasetId];
	const focusedId = currentDataset || null;

	useEffect(() => {
		const applyHash = () => {
			const hash = (window.location.hash || '').replace('#', '');
			if (HUB_SECTIONS.some(section => section.id === hash)) {
				setActiveSection(hash);
			}
		};
		applyHash();
		setDeleteConfirm('');
		window.addEventListener('hashchange', applyHash);
		return () => window.removeEventListener('hashchange', applyHash);
	}, [datasetId]);

	useEffect(() => {
		if (typeof window.indexedDB === 'undefined') {
			return undefined;
		}
		getOfflineChanges()
			.then(result => {
				if (result?.success) {
					setOfflineChanges(Array.isArray(result.offlineChanges) ? result.offlineChanges : []);
				}
			})
			.catch(() => setOfflineChanges([]));
		return undefined;
	}, [datasetId]);

	const selectSection = sectionId => {
		setActiveSection(sectionId);
		if (typeof window !== 'undefined' && window.location.hash.replace('#', '') !== sectionId) {
			window.history.replaceState(null, '', `#${sectionId}`);
		}
	};

	useEffect(() => {
		if (import.meta.env.DEV) {
			setRelatedUsersLoading(false);
			return undefined;
		}
		let cancelled = false;
		setRelatedUsersLoading(true);
		Utils.ajaxAsync('getRelatedUsers', {}, null, { suppressErrorAlert: true })
			.then(result => {
				if (!cancelled && result.success) {
					setRelatedUsers(Array.isArray(result.userList) ? result.userList : []);
				}
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) setRelatedUsersLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	const getProjectMembers = id => {
		const raw = permissions?.[id];
		if (Array.isArray(raw)) return raw;
		if (raw && typeof raw === 'object') return Object.values(raw);
		return [];
	};

	const buildRelatedUsersFromProjects = excludeDatasetId => {
		const byId = {};
		for (const user of relatedUsers) {
			byId[String(user.Id)] = {
				Id: user.Id,
				FirstName: user.FirstName || '',
				LastName: user.LastName || '',
				EmailAddress: user.EmailAddress || '',
				Invited: !!user.Invited,
				PreviousProjects: Array.isArray(user.PreviousProjects) ? [...user.PreviousProjects] : []
			};
		}
		for (const projectId of Object.keys(permissions || {})) {
			if (String(projectId) === String(excludeDatasetId)) continue;
			const projectName = datasets?.[projectId]?.Name || `Project ${projectId}`;
			for (const member of getProjectMembers(projectId)) {
				if (!member || member.UserId == null) continue;
				const id = String(member.UserId);
				if (!byId[id]) {
					byId[id] = {
						Id: member.UserId,
						FirstName: member.FirstName || '',
						LastName: member.LastName || '',
						EmailAddress: member.EmailAddress || '',
						Invited: false,
						PreviousProjects: []
					};
				}
				if (!byId[id].PreviousProjects.some(project => String(project.Id) === String(projectId))) {
					byId[id].PreviousProjects.push({ Id: projectId, Name: projectName });
				}
			}
		}
		return Object.values(byId).sort((a, b) => (a.EmailAddress || '').localeCompare(b.EmailAddress || ''));
	};

	const getAvailableUsersForProject = id => {
		const currentMembers = getProjectMembers(id);
		const existingIds = new Set(currentMembers.map(permission => String(permission.UserId)));
		const existingEmails = new Set(currentMembers.map(permission => (permission.EmailAddress || '').toLowerCase()));
		const search = userSearchTerm.trim().toLowerCase();
		return buildRelatedUsersFromProjects(id).filter(user => {
			if (existingIds.has(String(user.Id)) || existingEmails.has((user.EmailAddress || '').toLowerCase())) {
				return false;
			}
			if (!search) return true;
			const name = `${user.FirstName || ''} ${user.LastName || ''}`.toLowerCase();
			const email = (user.EmailAddress || '').toLowerCase();
			const projectNames = (user.PreviousProjects || []).map(project => (project.Name || '').toLowerCase()).join(' ');
			return name.includes(search) || email.includes(search) || projectNames.includes(search);
		});
	};

	if (!dataset) {
		return (
			<div className="paddedTabContent">
				<div className="setupEmptyState">
					<p>This project is not in your library.</p>
					<Link className="btn btn-default" to="/setup/">All projects</Link>
				</div>
			</div>
		);
	}

	const startDate = dataset.StartDate ? dayjs(dataset.StartDate).add(3, 'hours').valueOf() : '';
	const endDate = dataset.EndDate ? dataset.EndDate : '';
	const activityCodeTypes = dataset.activityCodeTypes && typeof dataset.activityCodeTypes === 'object'
		? dataset.activityCodeTypes
		: {};
	const areaPropertyOptions = Object.keys(activityCodeTypes).map(activityCodeTypeId => (
		<option key={activityCodeTypeId} value={activityCodeTypeId}>{activityCodeTypes[activityCodeTypeId].name}</option>
	));
	const areaProperty = dataset.areaProperty || '';
	const changes = offlineChanges.filter(change => String(change.datasetId) === String(datasetId));
	const projectMembers = getProjectMembers(datasetId);
	const libraryCount = Object.keys(datasets || {}).length;
	const formatLabel = sourceFormatLabel(dataset);
	const isFocused = String(focusedId) === String(datasetId);

	const updateStartDate = (id, date) => {
		updateDatasetMetadata(id, 'StartDate', date);
		Utils.ajax('updateStartDate', { dataset: id, startDate: date ? dayjs(date).format('YYYY-MM-DD') : '' }, dispatch);
	};
	const updateEndDate = (id, date) => {
		updateDatasetMetadata(id, 'EndDate', date);
		Utils.ajax('updateEndDate', { dataset: id, endDate: date ? dayjs(date).format('YYYY-MM-DD') : '' }, dispatch);
	};
	const updateRemainingDurationUnit = (id, remainingDurationUnit) => {
		updateDatasetMetadata(id, 'RemainingDurationUnit', remainingDurationUnit);
		Utils.ajax('updateRemainingDurationUnit', { dataset: id, remainingDurationUnit }, dispatch);
	};
	const updateDisplayTime = (id, displayTime) => {
		updateDatasetMetadata(id, 'DisplayTime', displayTime);
		Utils.ajax('updateDisplayTime', { dataset: id, displayTime }, dispatch);
	};
	const updateShowGanttHolidays = (id, show) => {
		updateDatasetMetadata(id, 'ShowGanttHolidays', show);
		Utils.ajax('updateShowGanttHolidays', { dataset: id, showGanttHolidays: show ? 'true' : 'false' }, dispatch);
	};
	const updateAllowNotStartDurationChanges = (id, allow) => {
		updateDatasetMetadata(id, 'AllowNotStartDurationChanges', allow);
		Utils.ajax('updateAllowNotStartDurationChanges', { dataset: id, allow }, dispatch);
	};
	const updateAllowOffline = (id, allowOffline) => {
		trackEvent('project', 'click', (allowOffline ? 'store' : 'remove') + ' offline dataset', id);
		updateDatasetMetadata(id, 'AllowOffline', allowOffline);
		Utils.ajax('updateAllowOffline', { dataset: id, allowOffline }, dispatch, result => {
			if (!result.success) return;
			if (allowOffline) {
				if (isOnline) storeOfflineDataset(dispatch, id);
				else dispatch(openAlert('Offline', 'You cannot download a project for offline use while offline.'));
			} else {
				removeOfflineDataset(id);
			}
		});
	};

	const inviteUser = () => {
		dispatch(openPrompt('Invite to SiteWalker:', email => {
			const trimmed = (email || '').trim();
			if (!trimmed) {
				dispatch(openAlert('Email needed', 'You must enter an email address to invite.'));
				return;
			}
			Utils.ajaxAsync('inviteUser', { email: trimmed, datasetId: String(datasetId) }, dispatch).then(result => {
				if (result && result.success) {
					dispatch(openAlert(
						'Invitation Sent',
						`An email invitation was sent to ${trimmed}. They can open this project directly from the link. A temporary password is included — ask them to change it under Account settings.`
					));
				}
			}).catch(() => {});
		}, '', 'Email address'));
	};

	const addUserToProject = user => {
		const existing = projectMembers.find(
			permission => permission.EmailAddress === user.EmailAddress || String(permission.UserId) === String(user.Id)
		);
		if (existing) {
			dispatch(openAlert('Already added', `${user.EmailAddress} already has permission to this project.`));
			return;
		}
		setUserPermission(user.Id, datasetId, 1, true);
	};

	let addUsersContent = null;
	if (relatedUsersLoading) {
		addUsersContent = <div className="addProjectUsersEmpty">Loading users…</div>;
	} else {
		const availableUsers = getAvailableUsersForProject(datasetId);
		if (!availableUsers.length) {
			addUsersContent = (
				<div className="addProjectUsersEmpty">
					{userSearchTerm.trim()
						? 'No matching users. Try a different filter or invite someone by email.'
						: 'No related users available to add. Invite someone by email to get started.'}
				</div>
			);
		} else {
			addUsersContent = (
				<Table condensed className="addProjectUsersTable">
					<thead>
						<tr>
							<th>Name</th>
							<th>Email</th>
							<th>Previous projects</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{availableUsers.map(user => {
							const displayName = [user.FirstName, user.LastName].filter(Boolean).join(' ') || '—';
							const previousProjects = (user.PreviousProjects || []).map(project => project.Name).filter(Boolean);
							return (
								<tr key={user.Id}>
									<td>{displayName}</td>
									<td>{user.EmailAddress}</td>
									<td className="addProjectUsersPrevious">{previousProjects.join(', ') || (user.Invited ? 'Invited previously' : '—')}</td>
									<td className="addProjectUsersAction">
										<Button bsSize="small" bsStyle="primary" onClick={() => addUserToProject(user)}>Add</Button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</Table>
			);
		}
	}

	return (
		<div className="projectHub">
			<div className="hubProjectSwitcher" aria-label="Open project">
				<span className="openStripLabel">Open project</span>
				<span className="openTab active">
					<span className="tabName">{dataset.Name}</span>
					<span className="sourceFormatBadge">{formatLabel}</span>
				</span>
				<Link className="openTab" to="/setup/">All projects</Link>
			</div>

			<div className="hubLayout">
				<nav className="hubNav" aria-label="Project sections">
					{HUB_SECTIONS.map(section => (
						<button
							type="button"
							key={section.id}
							className={classnames({ active: activeSection === section.id, danger: section.id === 'danger' })}
							onClick={() => selectSection(section.id)}>
							{section.label}
						</button>
					))}
				</nav>

				<div className="hubMain">
					<div className="hubTitleBar">
						<div>
							<h2>{dataset.Name}</h2>
							{dataset.p6ProjectNotice ? (
								<p className="alert alert-info" role="status">{dataset.p6ProjectNotice}</p>
							) : null}
							<div className="projectHubMeta">
								<span className="sourceFormatBadge">{formatLabel}</span>
								{dataset.Owned ? 'Owned by you' : `Shared by ${dataset.Owner || 'another user'}`}
								{dataset.StartDate && dataset.EndDate
									? ` · Timescale ${dayjs(dataset.StartDate).format('DD-MMM-YYYY')} → ${dayjs(dataset.EndDate).format('DD-MMM-YYYY')}`
									: ''}
							</div>
						</div>
						<div className="hubTitleActions">
							<Link className="btn btn-default btn-sm" to="/setup/">All projects</Link>
							<Button
								bsSize="small"
								bsStyle="primary"
								onClick={() => focusProject({ dispatch, navigate, datasetId, datasets })}>
								{isFocused ? 'Open in app' : 'Focus in app'}
							</Button>
						</div>
					</div>

					{activeSection === 'overview' && (
						<section>
							<h3>Overview</h3>
							<p className="hubLead">Identity and the toggles you change most often. Sibling open projects stay loaded.</p>
							<div className="setupFieldGrid">
								<div>
									<label className="hubFieldLabel">Project name</label>
									<div className="setupReadonlyValue">{dataset.Name}</div>
								</div>
								<div>
									<label className="hubFieldLabel">Source</label>
									<div className="setupReadonlyValue">{formatLabel === 'MSP' ? 'MS Project (.xml)' : 'Primavera P6 (.xer)'}</div>
								</div>
								<div>
									<label className="hubFieldLabel">Area activity code type</label>
									<FormControl
										className="setupInput"
										componentClass="select"
										value={areaProperty}
										onChange={event => updateAreaProperty(datasetId, event.target.value)}>
										<option value="">Choose Area Activity Code Type</option>
										{areaPropertyOptions}
									</FormControl>
								</div>
							</div>
							<div className="hubToggleList">
								<label className="hubToggleRow">
									<span>Display time and date</span>
									<input type="checkbox" checked={!!dataset.DisplayTime} onChange={e => updateDisplayTime(datasetId, e.target.checked)} />
								</label>
								<label className="hubToggleRow">
									<span>Show non-working time on Gantt</span>
									<input type="checkbox" checked={dataset.ShowGanttHolidays !== false} onChange={e => updateShowGanttHolidays(datasetId, e.target.checked)} />
								</label>
								<label className="hubToggleRow">
									<span>Allow TK_NotStart duration changes</span>
									<input type="checkbox" checked={!!dataset.AllowNotStartDurationChanges} onChange={e => updateAllowNotStartDurationChanges(datasetId, e.target.checked)} />
								</label>
								<label className="hubToggleRow">
									<span>Allow offline</span>
									<input type="checkbox" checked={!!dataset.AllowOffline} onChange={e => updateAllowOffline(datasetId, e.target.checked)} />
								</label>
							</div>
						</section>
					)}

					{activeSection === 'files' && (
						<section>
							<h3>Files &amp; map</h3>
							<p className="hubLead">Replace schedule, KML, and map overlay for this project only.</p>
							<div className="hubFileRow">
								<div>
									<strong>Schedule file</strong>
									<div className="projectHubMeta">{dataset.Name || 'None'}</div>
								</div>
								<Button className="inlineEditButton" onClick={() => setEditingDataset(datasetId, 'xer')}>Replace file</Button>
							</div>
							<div className="hubFileRow">
								<div>
									<strong>KML file</strong>
									<div className="projectHubMeta">{dataset.Kml || 'None'}</div>
								</div>
								<Button className="inlineEditButton" onClick={() => setEditingDataset(datasetId, 'kml')}>Edit KML</Button>
							</div>
							<div className="hubFileRow">
								<div>
									<strong>Overlay</strong>
									{dataset.North && dataset.South && dataset.East && dataset.West
										? (
											<div>
												<div className="projectHubMeta">{dataset.Overlay}</div>
												<img className="overlayPreview" src={`/datasets/${dataset.Id}/${dataset.Overlay}`} alt="" />
											</div>
										)
										: <div className="projectHubMeta">Please upload an overlay within the KML file.</div>}
								</div>
								{dataset.North && dataset.South && dataset.East && dataset.West
									? <Button onClick={() => setEditingDataset(datasetId, 'overlay')}>Edit overlay</Button>
									: null}
							</div>
						</section>
					)}

					{activeSection === 'schedule' && (
						<section>
							<h3>Schedule</h3>
							<p className="hubLead">Timescale used by this project’s Gantt — independent of other open projects.</p>
							<Table className="projectSetupTable">
								<tbody>
									<tr>
										<td>Timescale Start</td>
										<td className={classnames('datePickerColumn', { empty: startDate === '' })}>
											<Flatpickr
												className="datePicker startDate form-control"
												options={{
													dateFormat: 'd-M-Y',
													disableMobile: 'true',
													onChange: (dates, string, flatpickr) => {
														flatpickr.close();
														updateStartDate(datasetId, dates[0].valueOf());
													}
												}}
												value={startDate} />
										</td>
									</tr>
									<tr>
										<td>Timescale Finish</td>
										<td className={classnames('datePickerColumn', { empty: endDate === '' })}>
											<Flatpickr
												className="datePicker endDate form-control"
												options={{
													dateFormat: 'd-M-Y',
													disableMobile: 'true',
													onChange: (dates, string, flatpickr) => {
														flatpickr.close();
														updateEndDate(datasetId, dates[0].valueOf());
													}
												}}
												value={endDate} />
										</td>
									</tr>
									<tr>
										<td>Remaining Duration Unit</td>
										<td>
											<FormControl
												componentClass="select"
												value={dataset.RemainingDurationUnit}
												onChange={event => updateRemainingDurationUnit(datasetId, event.target.value)}>
												<option value="hours">Hours</option>
												<option value="days">Days</option>
											</FormControl>
										</td>
									</tr>
								</tbody>
							</Table>
						</section>
					)}

					{activeSection === 'versions' && (
						<section>
							<h3>Schedule versions</h3>
							<p className="hubLead">Display snapshot, baseline, and weekly uploads.</p>
							<ScheduleVersionList datasetId={datasetId} datasetName={dataset.Name} />
						</section>
					)}

					{activeSection === 'people' && (
						<section>
							<h3>People &amp; permissions</h3>
							<p className="hubLead">Members of this project only — not shared across the open set.</p>
							<div className="labeledCheckboxGroup">
								{projectMembers.map(permission => (
									<Panel key={permission.UserId}>
										<Panel.Heading>
											<Panel.Title toggle>{permission.EmailAddress}</Panel.Title>
										</Panel.Heading>
										<Panel.Collapse>
											<Panel.Body>
												<LabeledCheckbox
													text="Access"
													checked={true}
													onChange={() => {
														dispatch(openAlert('Remove User?', `Are you sure you want to remove ${permission.EmailAddress} from this project?`, [{
															bsStyle: 'danger',
															text: 'Yes, remove.',
															action: () => setUserPermission(permission.UserId, datasetId, 1, false)
														}, {
															text: 'No, cancel.',
															action: () => true
														}]));
													}} />
												<LabeledCheckbox
													text="Export"
													checked={'5' in permission.Permissions}
													onChange={e => setUserPermission(permission.UserId, datasetId, 5, e.target.checked)} />
												<LabeledCheckbox
													text="Update KML"
													checked={'3' in permission.Permissions}
													onChange={e => setUserPermission(permission.UserId, datasetId, 3, e.target.checked)} />
												{dataset.AllowNotStartDurationChanges
													? (
														<LabeledCheckbox
															text="Change TK_NotStart Duration"
															checked={'7' in permission.Permissions}
															onChange={e => setUserPermission(permission.UserId, datasetId, 7, e.target.checked)} />
													)
													: null}
											</Panel.Body>
										</Panel.Collapse>
									</Panel>
								))}
							</div>
							{dataset.Owned
								? (
									<div className="addProjectUsers">
										<div className="addProjectUsersHeader">
											<div>
												<h4>Add users</h4>
												<p>Select from people you have invited or worked with on previous projects.</p>
											</div>
											<Button className="setupPrimaryBtn" onClick={inviteUser}>Invite by email</Button>
										</div>
										<FormControl
											type="text"
											className="setupInput addProjectUsersSearch"
											placeholder="Filter by name or email..."
											value={userSearchTerm}
											onChange={event => setUserSearchTerm(event.target.value)} />
										{addUsersContent}
									</div>
								)
								: null}
						</section>
					)}

					{activeSection === 'offline' && (
						<section>
							<h3>Offline</h3>
							<p className="hubLead">Each open project can have its own offline copy and change queue.</p>
							<div className="hubFileRow">
								<div>
									<strong>Allow offline</strong>
									<div className="projectHubMeta">{dataset.AllowOffline ? 'Dataset can be stored for field use' : 'Offline is off for this project'}</div>
								</div>
								<input
									type="checkbox"
									checked={!!dataset.AllowOffline}
									onChange={e => updateAllowOffline(datasetId, e.target.checked)} />
							</div>
							{dataset.AllowOffline
								? (
									<div className="hubFileRow">
										<div>
											<strong>Offline change queue ({changes.length})</strong>
											<div className="projectHubMeta">{changes.length ? `${changes.length} waiting to sync` : 'No changes queued'}</div>
										</div>
									</div>
								)
								: null}
							{dataset.AllowOffline && changes.length
								? changes.map((change, index) => (
									<Well key={index}>
										Activity: {change.data === 'Error' ? 'Error parsing queued change' : (change.data.name ? change.data.name : change.data.activityId)}
									</Well>
								))
								: null}
						</section>
					)}

					{activeSection === 'danger' && (
						<section>
							<h3>Danger zone</h3>
							<p className="hubLead">Delete removes the project from the library; other open projects are unaffected.</p>
							<div className="hubDangerZone">
								<p>Deleting removes this project for you and shared members. You must keep at least one project.</p>
								<p>
									Type the project name <strong>{dataset.Name}</strong> to enable delete.
								</p>
								<FormControl
									type="text"
									className="setupInput setupDangerZoneConfirm"
									value={deleteConfirm}
									aria-label={`Type ${dataset.Name} to enable delete`}
									onChange={event => setDeleteConfirm(event.target.value)} />
								<Button
									bsStyle="danger"
									disabled={deleteConfirm !== dataset.Name}
									onClick={() => {
										if (libraryCount <= 1) {
											dispatch(openAlert('Project Count', "You can't delete this project because you must have at least 1 project assigned to you."));
											return;
										}
										dispatch(openAlert('Delete this project permanently?', `This cannot be undone from the SiteWalker UI. Delete “${dataset.Name}”?`, [{
											bsStyle: 'danger',
											text: 'Yes, delete it permanently.',
											action: () => {
												deleteProject(datasetId);
												navigate('/setup/');
											}
										}, {
											text: 'No, keep it.',
											action: () => true
										}]));
									}}>
									Delete project
								</Button>
							</div>
						</section>
					)}
				</div>
			</div>
		</div>
	);
};

ProjectHub.propTypes = {
	datasetId: PropTypes.string.isRequired,
	permissions: PropTypes.object,
	setUserPermission: PropTypes.func,
	updateDatasetMetadata: PropTypes.func,
	deleteProject: PropTypes.func,
	setEditingDataset: PropTypes.func,
	isOnline: PropTypes.bool,
	updateAreaProperty: PropTypes.func
};

export default ProjectHub;
